<template>
  <div class="w-screen h-screen lg:p-10 bg-pure-white overflow-hidden">
    <div class="bl-auth-background px-4 lg:p-0">
      <div class="bl-auth-card rounded-3xl bg-pure-white p-6 lg:p-12">
        <form
          v-if="!hasAnotherDeviceSignedIn"
          @submit.prevent="onSignin"
          class="lg:w-128"
        >
          <div class="text-center space-y-3 mb-6">
            <div class="font-logo text-light-purple font-bold text-4xl">
              Blüm
            </div>
            <BlTextBody2
              text="Streamline your ABA therapy data collection process and make your sessions more effective."
            />
          </div>

          <BlInputText
            class="mb-4"
            :input="emailInput"
            @changed="inputHandler"
          />
          <BlInputText
            class="mb-4"
            :input="passwordInput"
            @changed="inputHandler"
          />

          <div class="flex items-center justify-between mb-4">
            <div
              class="flex items-center gap-3 cursor-pointer"
              @click="onChangeRememberMe"
            >
              <BlInputCheck
                :checked="rememberMe"
                @changed="onChangeRememberMe"
              />

              <BlTextBody2 text="Remember me" />
            </div>
            <BlInputButton
              type="plain"
              label="Forgot Password?"
              @clicked="goForgotPassword"
            />
          </div>

          <BlInputButton
            class="w-full"
            buttonType="submit"
            label="Sign In"
            :loading="submitLoading"
          />
        </form>

        <div
          v-if="hasAnotherDeviceSignedIn"
          class="lg:w-128 flex flex-col gap-4"
        >
          <div class="flex flex-col gap-3">
            <div class="font-logo text-light-purple font-bold text-2xl">
              Blüm
            </div>
            <BlTextTitle4
              text="Sign out from another device to access Blüm"
              class="text-light-purple"
            />
            <div>
              <BlTextBody2
                text="You've reached the maximum number of devices allowed for"
              />
              <BlTextBody2 :text="`${user.email}.`" class="font-bold" />
              <BlTextBody2
                text="To sign in here, please choose a device to log out from. Once signed out, you'll be able to continue on this device."
              />
            </div>
          </div>

          <div
            v-for="device in activeDevices"
            :key="device.id + device.updated_at"
            class="py-3 flex items-center justify-between border-b border-slate-300"
          >
            <div class="flex items-center gap-3 truncate">
              <BlIcon
                v-if="device.status === 'active'"
                icon="check-circle"
                color="grass-6"
                class="h-6 w-6"
              />
              <BlIcon
                v-if="device.status === 'inactive'"
                icon="check-circle"
                color="slate-6"
                class="h-6 w-6"
              />
              <div class="flex flex-col gap-1 truncate">
                <div v-if="device.device_details" class="truncate">
                  <BlTextSubtitle3
                    v-if="device.device_details.os_name"
                    :text="device.device_details.os_name"
                  />
                  <BlTextSubtitle3
                    v-if="device.device_details.os_version"
                    :text="device.device_details.os_version"
                  />
                  <BlTextSubtitle3
                    v-if="device.device_details.device_name"
                    :text="device.device_details.device_name"
                  />
                </div>
                <div>
                  <BlTextBody2 text="Last accessed:" />
                  <BlTextBody2
                    :text="
                      displayDate({
                        date: device.last_accessed_at,
                        format: 'DD MMM YYYY'
                      })
                    "
                  />
                </div>
              </div>
            </div>
            <BlInputButton
              v-if="device.status === 'active'"
              label="Sign out"
              type="outlined"
              dense
              @clicked="onSignoutDevice(device)"
            />
            <BlInputButton
              v-if="device.status === 'inactive'"
              label="Signed out"
              type="outlined"
              dense
              disabled
            />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <BlInputButton
              label="Back to sign-in"
              type="outlined"
              @clicked="hasAnotherDeviceSignedIn = false"
            />
            <BlInputButton
              label="Continue using this device"
              :disabled="
                activeDevices.filter(i => i.status === 'active').length > 1
              "
              :loading="submitLoading"
              @clicked="onSignin"
            />
          </div>
          <!-- <pre>{{ JSON.stringify(user, null, 2) }}</pre> -->
          <!-- <div class="break-words">
            <pre>{{ JSON.stringify(activeDevices, null, 2) }}</pre>
          </div> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlInputText from "../BlInputText.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputCheck from "../BlInputCheck.vue";

export default {
  name: "Signin",
  components: {
    BlTextTitle4,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputText,
    BlInputButton,
    BlInputCheck
  },
  data() {
    return {
      submitLoading: false,
      emailInput: {
        placeholder: "Type your email here",
        label: "Email",
        inputType: "email",
        model: "",
        error: ""
      },
      passwordInput: {
        placeholder: "Type your password here",
        label: "Password",
        inputType: "password",
        model: "",
        error: ""
      },
      rememberMe: false,
      //
      hasAnotherDeviceSignedIn: false,
      user: null,
      activeDevices: [],
      //
      deviceType: "",
      appType: "browser",
      deviceId: "",
      deviceDetails: {},
      osStore: [
        { name: "Windows Phone", value: "Windows Phone", version: "OS" },
        { name: "Windows", value: "Win", version: "NT" },
        { name: "iOS", value: "iPhone", version: "OS" },
        { name: "iOS", value: "iPad", version: "OS" },
        { name: "Kindle", value: "Silk", version: "Silk" },
        { name: "Android", value: "Android", version: "Android" },
        { name: "PlayBook", value: "PlayBook", version: "OS" },
        { name: "BlackBerry", value: "BlackBerry", version: "/" },
        { name: "Macintosh", value: "Mac", version: "OS X" },
        { name: "Linux", value: "Linux", version: "rv" },
        { name: "Palm", value: "Palm", version: "PalmOS" }
      ],
      browserStore: [
        { name: "Chrome", value: "Chrome", version: "Chrome" },
        { name: "Firefox", value: "Firefox", version: "Firefox" },
        { name: "Safari", value: "Safari", version: "Version" },
        { name: "Internet Explorer", value: "MSIE", version: "MSIE" },
        { name: "Opera", value: "Opera", version: "Opera" },
        { name: "BlackBerry", value: "CLDC", version: "CLDC" },
        { name: "Mozilla", value: "Mozilla", version: "Mozilla" }
      ]
    };
  },
  mounted() {
    this.hasAnotherDeviceSignedIn = false;
    this.user = null;
    this.activeDevices = [];
    //
    const header = [
      // window.navigator.platform,
      "",
      window.navigator.userAgent,
      window.navigator.appVersion,
      window.navigator.vendor,
      window.opera
    ];
    const agent = header.join(" ");
    const os = this.matchItem(agent, this.osStore);
    const browser = this.matchItem(agent, this.browserStore);

    const userAgent = window.navigator.userAgent;
    //
    this.deviceType = /Mobile|Android|iPhone|iPad/i.test(userAgent)
      ? "mobile"
      : "desktop";
    // this.deviceId = userAgent
    //   .slice(userAgent.indexOf("(") + 1, userAgent.indexOf(")"))
    //   .replaceAll(" ", "_");
    this.deviceId = agent.replaceAll(" ", "_");
    this.deviceDetails = {
      device_name: this.getDeviceName(),
      os_name: os.name,
      os_version: os.version,
      browser_name: browser.name,
      browser_version: browser.version
    };

    // console.log("device_type", this.deviceType);
    // console.log("device_id", this.deviceId);
    // console.log("device_details", this.deviceDetails);
  },
  methods: {
    ...mapActions(["createCurrentUser", "showToast"]),
    displayDate,
    matchItem(string, data) {
      let i = 0;
      let j = 0;
      let regex;
      let regexv;
      let match;
      let matches;
      let version;

      for (i = 0; i < data.length; i += 1) {
        regex = new RegExp(data[i].value, "i");
        match = regex.test(string);
        if (match) {
          regexv = new RegExp(data[i].version + "[- /:;]([\\d._]+)", "i");
          matches = string.match(regexv);
          version = "";
          if (matches) {
            if (matches[1]) {
              matches = matches[1];
            }
          }
          if (matches) {
            matches = matches.split(/[._]+/);
            for (j = 0; j < matches.length; j += 1) {
              if (j === 0) {
                version += matches[j] + ".";
              } else {
                version += matches[j];
              }
            }
          } else {
            version = "0";
          }
          return {
            name: data[i].name,
            version: parseFloat(version)
          };
        }
      }
      return { name: "unknown", version: 0 };
    },
    // get device name for android
    getAndroidDeviceName() {
      const androidUserAgentString = window.navigator.userAgent.slice(
        window.navigator.userAgent.indexOf("Android")
      );
      const androidDeviceName = androidUserAgentString.slice(
        androidUserAgentString.indexOf("; ") + 1,
        androidUserAgentString.indexOf(")")
      );
      if (androidDeviceName) {
        return androidDeviceName.trim().split(" ")[0];
      }

      return "Android";
    },
    // get device name for ios
    getIosDeviceName() {
      const iosDeviceMapping = new Map([
        ["320x480", "iPhone 4S, 4, 3GS, 3G, 1st gen"],
        ["320x568", "iPhone 5, SE 1st Gen,5C, 5S"],
        ["375x667", "iPhone SE 2nd Gen, 6, 6S, 7, 8"],
        ["375x812", "iPhone X, XS, 11 Pro, 12 Mini, 13 Mini"],
        ["390x844", "iPhone 13, 13 Pro, 12, 12 Pro"],
        ["414x736", "iPhone 8+"],
        ["414x896", "iPhone 11, XR, XS Max, 11 Pro Max"],
        ["428x926", "iPhone 13 Pro Max, 12 Pro Max"],
        ["476x847", "iPhone 7+, 6+, 6S+"],
        ["744x1133", "iPad Mini 6th Gen"],
        [
          "768x1024",
          "iPad Mini (5th Gen), iPad (1-6th Gen), iPad Pro (1st Gen 9.7), Ipad Mini (1-4), iPad Air(1-2)  "
        ],
        ["810x1080", "iPad 7-9th Gen"],
        ["820x1180", "iPad Air (4th gen)"],
        ["834x1194", "iPad Pro (3-5th Gen 11)"],
        ["834x1112", "iPad Air (3rd gen), iPad Pro (2nd gen 10.5)"],
        ["1024x1366", "iPad Pro (1-5th Gen 12.9)"]
      ]);
      const screenResolution = `${window.screen.width}x${window.screen.height}`;
      const device = iosDeviceMapping.get(screenResolution);
      return device || "iPhone";
    },
    // get device name for desktop
    getDesktopDeviceName() {
      const desktopDeviceMapping = new Map([
        ["Win32", "Windows"],
        ["Linux", "Linux"],
        ["MacIntel", "Mac OS"]
      ]);
      const platform =
        navigator.userAgentData.platform ||
        window.navigator.platform ||
        "unknown";
      const device = desktopDeviceMapping.get(platform);
      return device || "";
    },
    getDeviceName() {
      let device = "";

      // check if mobile device
      const isMobileDevice = window.navigator.userAgent
        .toLowerCase()
        .includes("mobi");

      if (isMobileDevice) {
        if (window.navigator.userAgent.includes("Android")) {
          device = this.getAndroidDeviceName();
        } else {
          device = this.getIosDeviceName();
        }
      } else {
        // device = this.getDesktopDeviceName();
        // device = "Desktop";
        device = "";
      }

      return device;
    },
    //
    inputHandler(payload) {
      payload.input.model = payload.value;
      payload.input.error = "";
    },
    onChangeRememberMe() {
      this.rememberMe = !this.rememberMe;
    },
    onSignin() {
      const email = this.emailInput.model;
      const password = this.passwordInput.model;
      if (!email || !password) {
        if (!email) this.emailInput.error = "Email is required.";
        if (!password) this.passwordInput.error = "Password is required.";
        return;
      }
      const data = {
        email,
        password,
        device: {
          device_type: this.deviceType, // mobile or desktop
          app_type: this.appType, // browser or mobile_app
          device_id: `${email}#${this.deviceId}`,
          device_details: this.deviceDetails
        }
      };

      this.submitLoading = true;
      this.$http.plain
        .post("/signin", data)
        .then(response => this.signinSuccessful(response))
        .catch(error => this.signinFailed(error));
    },
    async signinSuccessful(response) {
      if (!response.data.csrf) {
        this.signinFailed(response);
        return;
      }
      localStorage.setItem("csrf", response.data.csrf);
      localStorage.setItem("access", response.data.access);
      await this.createCurrentUser(response.data.user);
      const title = "You have successfully signed in";
      this.showToast({ title, message: "", type: "success" });
      const redirect = this.$route.query.redirect
        ? this.$route.query.redirect
        : "/clients";
      setTimeout(() => {
        location.replace(redirect);
        this.submitLoading = false;
      }, 300);
    },
    signinFailed(error) {
      const payload = (error.response && error.response.data) || {};

      if (payload.active_devices) {
        this.handleAnotherDevice(error.response.data);
        this.submitLoading = false;
        return;
      }

      let title = "Sign in failed";
      let message = payload.error;
      if (message === "Token is invalid") {
        title = "Login failed";
        message =
          "Activate your account by logging in with the unique sign-in <br /> URL provided by the admin.";
      } else if (
        message ===
        "Your account has been deactivated. Please contact the Admin for further assistance."
      ) {
        title = "Account deactivated";
        message =
          "Your account has been deactivated. Please contact the Admin <br /> for further assistance.";
      }
      this.showToast({ title, message, type: "error" });
      localStorage.removeItem("csrf");
      localStorage.removeItem("access");
      this.submitLoading = false;
    },
    handleAnotherDevice(data) {
      this.user = data.user;
      this.activeDevices = data.active_devices;
      this.hasAnotherDeviceSignedIn = true;
    },
    // fetchActiveDevices() {
    //   // GET /api/v1/users/:id/devices
    //   this.$http.plain
    //     .get(`/api/v1/users/${this.user.id}/devices`)
    //     .then(response => {
    //       console.log("fetchActiveDevices");
    //       console.log(response);
    //     });
    // },
    onSignoutDevice(device) {
      this.$http.plain
        .delete(`/signout_by_device?device_id=${device.id}`)
        .then(response => {
          console.log("onSignoutDevice");
          const idx = this.activeDevices.findIndex(i => i.id === device.id);
          const arr = [...this.activeDevices];
          arr[idx] = response.data;
          this.activeDevices = arr;

          const title = "Device signed out";
          let message = "Success! You have signed out from";
          if (device.device_details.os_name) {
            message += ` ${device.device_details.os_name}`;
          }
          if (device.device_details.os_version) {
            message += ` ${device.device_details.os_version}`;
          }
          if (device.device_details.device_name) {
            message += ` ${device.device_details.device_name}`;
          }
          this.showToast({ title, message, type: "success" });
        });
    },
    goForgotPassword() {
      return this.$router.push({ name: "ForgotPassword" });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/auth/Signin.vue